import {Button, DetailsCard, Loading, PageHeader, Pagination, TextRegular} from '@hm/fundamental'

import {ReportFilters, ReportsTable} from '../components'
import type {SortDirection} from '../helpers'
import {useRiskSurveyTranslation} from '../hooks'
import type {PaginatedResponse, ReportSummaryDto} from '../types'

type KpiData = {
  total: number
  new: number
  pending: number
  inProgress: number
  completed: number
  rejected: number
}

type FilterProps = {
  plantFilter: string[]
  onPlantFilterChange: (values: string[]) => void
  plantOptions: {value: string; label: string}[]
  includeArchived: boolean
  onIncludeArchivedChange: (value: boolean) => void
}

type TableProps = {
  showCountry: boolean
  onSortChange: (sortBy: string, sortDir: SortDirection) => void
  currentUserId?: string
  onEditSite: (report: ReportSummaryDto) => void
  onArchive: (reportId: string) => void
  onUnarchive: (reportId: string) => void
  onExport: (reportId: string, siteName: string) => void
  onReportClick: (report: ReportSummaryDto) => void
}

type RiskSurveyOverviewPageProps = {
  data: PaginatedResponse<ReportSummaryDto> | undefined
  kpi: KpiData
  isLoading: boolean
  isError: boolean
  onPageChange: (page: number, pageSize: number) => void
  onImport: () => void
  canManage: boolean
  filterProps: FilterProps
  tableProps: TableProps
}

export const RiskSurveyOverviewPage = ({
  data,
  kpi,
  isLoading,
  isError,
  onPageChange,
  onImport,
  canManage,
  filterProps,
  tableProps
}: RiskSurveyOverviewPageProps) => {
  const {t} = useRiskSurveyTranslation()

  return (
    <div className="flex flex-col gap-6 px-6 py-6 w-full max-w-full min-w-0 box-border overflow-x-clip">
      <PageHeader
        aside={
          canManage ? (
            <Button variant="secondary" onClick={onImport} data-cy="import-button">
              {t('plant.import')}
            </Button>
          ) : undefined
        }
      >
        <span className="text-foreground_inverted">{t('overview.title')}</span>
      </PageHeader>
      <div className="flex flex-col gap-6 w-full">
        <DetailsCard className="px-4 py-2">
          <ReportFilters
            plantFilter={filterProps.plantFilter}
            onPlantFilterChange={filterProps.onPlantFilterChange}
            plantOptions={filterProps.plantOptions}
            kpiTotal={kpi.total}
            kpiNew={kpi.new}
            kpiPending={kpi.pending}
            kpiInProgress={kpi.inProgress}
            kpiCompleted={kpi.completed}
            kpiRejected={kpi.rejected}
            includeArchived={filterProps.includeArchived}
            onIncludeArchivedChange={filterProps.onIncludeArchivedChange}
          />
        </DetailsCard>

        <DetailsCard>
          {isLoading ? (
            <Loading variant="primary" size="large" />
          ) : isError ? (
            <TextRegular className="p-4">{t('common.error')}</TextRegular>
          ) : (
            <>
              <ReportsTable
                reports={data?.items ?? []}
                showCountry={tableProps.showCountry}
                onSortChange={tableProps.onSortChange}
                currentUserId={tableProps.currentUserId}
                onEditSite={tableProps.onEditSite}
                onArchive={tableProps.onArchive}
                onUnarchive={tableProps.onUnarchive}
                onExport={tableProps.onExport}
                onRowClick={tableProps.onReportClick}
              />
              {data && data.totalPages > 1 && (
                <div className="mt-4 p-4">
                  <Pagination
                    label={t('common.page')}
                    totalItems={data.totalCount}
                    defaultPage={data.page}
                    defaultPageSize={data.pageSize}
                    onChange={onPageChange}
                  />
                </div>
              )}
            </>
          )}
        </DetailsCard>
      </div>
    </div>
  )
}
